/* eslint-disable max-len */
import { SwaggerlessContext } from 'Context/SwaggerlessContext';
import React, { Component } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Button } from '@material-ui/core';
import { QueryVal, addQuery } from 'Helpers/Query';
import LookalikeLabel from 'Components/UI/BehavioralFormElement/LabelDecorator/Lookalike';
import Text from 'Components/UI/BehavioralFormElement/NormalizedInput/Text';
import OverflowWrapper from 'Components/UI/OverflowWrapper';
import CurrencyFormat from 'Components/Format/Currency';
import { formatPhoneNumber } from 'Helpers/Formats';
import { getBirthdayAndAge } from 'Helpers/Client';
import { withHouseholdContextAdapter } from 'Context/SimpleHouseholdContext';
import ClientInfoButton from 'Components/AdvisorApp/Header/ClientInfoButton';
import PaymentModal from 'Components/Modals/PaymentModal/Admin';
import {
  displayErrorMessage,
  displaySuccess,
} from 'Helpers/FacetNotificationManager';
import SecondaryClientLabelPicker
  from 'Components/AdvisorApp/Viewport/Household/LandingPage/Demographics/SecondaryClientLabelPicker';
import FontAwesome from 'Components/UI/FontAwesome/FontAwesome';
import IconField from './IconField';
import TimeZone from './TimeZoneSelector';
import UserEmailAddressClipboard from './Clipboard';
import './index.css';
import { Tooltip } from '../../../../../UI/Tooltip';
import { copyToClipboard } from '../../../../../../Helpers/Utils';

function getClientPreferredEmail(client) {
  const emails = client
    ?.attr('emailAddresses')
    ?.resources()
    .list() || [];

  const preferredEmail = emails.find(v => v.attr('isPreferred') === 'true' && v.attr('type') !== 'generated');
  if (preferredEmail) {
    return preferredEmail.attr('address');
  }

  const primaryEmail = emails.find(v => v.attr('isPrimary') === 'true' && v.attr('type') !== 'generated');
  if (primaryEmail) {
    return primaryEmail.attr('address');
  }

  return null;
}

function getClientPhoneNumber(client) {
  const phoneNumbers = client
    ?.attr('phoneNumbers')
    ?.resources()
    .list() || [];
  const primaryPhoneNumber = phoneNumbers.find(
    v => v.attr('isPrimary') === 'true',
  );

  if (primaryPhoneNumber) {
    return primaryPhoneNumber.attr('phoneNumber');
  }

  return null;
}

class Demographics extends Component {
  constructor(props) {
    super(props);

    this.state = {
      pronunciationValue: props.primaryClient
        ? props.primaryClient.attr('pronunciation')
        : '',
      showPaymentModal: false,
      dependentsCollection: null,
      dependentsLink: null,
      nameTooltipTitle: 'Click to copy client name',
      phoneTooltipTitle: 'Click to copy phone number',
      spouseNameTooltipTitle: 'Click to copy spouse name',
    };

    this.getInitials = this.getInitials.bind(this);
    this.onUpdate = this.onUpdate.bind(this);
    this.onWrite = this.onWrite.bind(this);
    this.getDependents = this.getDependents.bind(this);
    this.setShowPaymentModal = this.setShowPaymentModal.bind(this);
  }

  componentDidMount() {
    this.getDependents();
  }

  componentDidUpdate(prevProps) {
    const { householdResource: currentHousehold } = this.props;
    const { householdResource: previousHousehold } = prevProps;
    if (previousHousehold?.id() !== currentHousehold?.id()) {
      this.getDependents();
    }
  }

  onUpdate(name, pronunciationValue) {
    this.setState({ pronunciationValue });
  }

  onWrite() {
    const { updateClient } = this.context;
    const { pronunciationValue } = this.state;
    const { primaryClient } = this.props;

    const pc = primaryClient.export();
    pc.attributes.pronunciation = pronunciationValue;

    updateClient(pc, {
      id: primaryClient.id(),
      householdId: QueryVal('householdId'),
    }); // todo: fix this
  }

  getDependents() {
    const { householdResource } = this.props;
    this.setState({
      dependentsLink: householdResource
        .relationship('dependents')
        .link('related')
        .url(),
    });
    if (householdResource.relationship('dependents')) {
      householdResource.relationship('dependents')
        .link('related')
        .Get()
        .then((coll) => {
          this.setState({
            dependents: coll
              .resources()
              .list()
              .filter(r => r.type() === 'child'),
          });
        });
    }
  }

  setShowPaymentModal(showPaymentModal) {
    this.setState(prevState => ({
      ...prevState,
      showPaymentModal,
    }));
  }

  setPronunciation() {
    const { primaryClient } = this.props;
    this.setState({
      pronunciationValue: primaryClient.attr('pronunciation'),
    });
  }

  getInitials() {
    const { primaryClient } = this.props;

    const firstName = primaryClient.attr('firstName');
    const lastName = primaryClient.attr('lastName');

    const fi = firstName.substring(0, 1);
    const li = lastName.substring(0, 1);
    return `${fi}${li}`.toUpperCase();
  }

  render() {
    const {
      className,
      householdResource,
      primaryClient,
      spouseClient,
    } = this.props;

    const { dependents, dependentsLink } = this.state;

    if (!householdResource) {
      return <div>Loading...</div>;
    }

    const { showPaymentModal } = this.state;
    const { nameTooltipTitle, phoneTooltipTitle, spouseNameTooltipTitle } = this.state;

    const billingRate = householdResource.attr('billingRate');
    const billingFrequency = householdResource.attr('billingFrequency');
    const city = householdResource.attr('city');
    const state = householdResource.attr('state');
    const hasPaymentError = householdResource.attr('hasPaymentError') === 'true';
    const paymentErrorMessage = householdResource.attr('paymentErrorMessage');
    const cardLastFour = householdResource.attr('cardLastFour');
    const paymentAttemptedOn = householdResource.attr('paymentAttemptedOn');
    const paymentAmountRemaining = householdResource.attr(
      'paymentAmountRemaining',
    );
    const paymentMethod = householdResource.attr('paymentMethod');
    const maritalStatus = householdResource.attr('maritalStatus');

    const firstName = primaryClient.attr('firstName');
    const lastName = primaryClient.attr('lastName');
    const nickname = primaryClient.attr('nickname');

    const formattedName = `${nickname || firstName} ${lastName}`;

    const birthDate = primaryClient.attr('birthDate');
    const householdId = primaryClient.attr('householdId');

    const phoneNumber = getClientPhoneNumber(primaryClient);
    const primaryClientEmail = getClientPreferredEmail(primaryClient);
    const secondaryClient = spouseClient;
    const secondaryClientEmail = getClientPreferredEmail(secondaryClient);

    const secondaryClientLabel = (() => {
      switch (maritalStatus) {
        case 'married':
          return 'Spouse';
        default:
          return 'Significant Other';
      }
    })();

    const togglePaymentModal = () => this.setShowPaymentModal(!showPaymentModal);

    const { pronunciationValue } = this.state;

    const determinePaymentIssueText = () => {
      switch (paymentMethod) {
        case 'Credit Card':
          return 'Credit Card Issues';
        case 'Account Debit':
          return 'Account Debit Issues';
        default:
          return 'Payment Issues';
      }
    };

    const Avatar = () => (
      <span className="initialsWrapper">
        {!hasPaymentError ? (
          <span className="initials">{this.getInitials()}</span>
        ) : (
          <Button
            color="error"
            onClick={togglePaymentModal}
            className="error-avatar"
          >
            <div>
              <FontAwesome
                className="error-icon"
                icon="exclamation-triangle"
                test="total"
              />
            </div>
            {determinePaymentIssueText(paymentMethod)}
          </Button>
        )}
      </span>
    );

    let infoCount = 1;
    infoCount += (primaryClientEmail || 0) && 1;
    infoCount += (phoneNumber || 0) && 1;
    infoCount += (secondaryClient || 0) && 1;
    infoCount += (dependents || 0) && 1;
    infoCount += (paymentAmountRemaining || 0) && 1;

    const width = Math.ceil(infoCount / 2) * 320;

    return (
      <div className={classNames('Demographics', className, {
        'bottom-margin': Boolean(birthDate),
      })}
      >
        {showPaymentModal && (
          <PaymentModal
            cardLastFour={cardLastFour}
            paymentAttemptedOn={paymentAttemptedOn}
            paymentAmountRemaining={paymentAmountRemaining}
            errorMessage={paymentErrorMessage}
            onSuccess={() => {
              displaySuccess('Successfully updated payment information');
              togglePaymentModal();
            }}
            onFailure={(err) => {
              displayErrorMessage(err, 'Failed to update payment information');
            }}
            close={togglePaymentModal}
          />
        )}
        <div className="client">
          <Avatar />
          <span className="clientText">
            <Tooltip
              title={nameTooltipTitle}
              onVisibleChange={(visible) => {
                if (!visible) {
                  this.setState({ nameTooltipTitle: 'Click to copy client name' });
                }
              }}
              placement="right"
            >
              <span
                className="name clickable"
                onClick={() => {
                  copyToClipboard(formattedName);
                  this.setState({ nameTooltipTitle: 'Copied' });
                }}
                role="presentation"
              >
                {formattedName}
              </span>
            </Tooltip>
            <LookalikeLabel>
              <Text
                name="pronunciation"
                placeholder="Add pronunciation"
                value={pronunciationValue}
                onChange={this.onUpdate}
                onBlur={this.onWrite}
              />
            </LookalikeLabel>
            {birthDate && <p>{getBirthdayAndAge(birthDate)}</p>}
            <div>
              <p>{`${primaryClient.attr('firstName')} ${primaryClient.attr('lastName')} [${primaryClient.id()}]`}</p>
              {secondaryClient && <p>{`${secondaryClient.attr('firstName')} ${secondaryClient.attr('lastName')} [${secondaryClient.id()}]`}</p>}
            </div>
          </span>

        </div>
        <ClientInfoButton
          householdId={QueryVal('householdId') || householdId}
        />
        <div className="info" style={{ width }}>
          <UserEmailAddressClipboard
            email={primaryClientEmail}
            spouseEmail={secondaryClientEmail}
            allAddresses={primaryClient
              .attr('emailAddresses')
              .resources()
              .list()}
          />
          {phoneNumber && (
            <Tooltip
              title={phoneTooltipTitle}
              onVisibleChange={(visible) => {
                if (!visible) {
                  this.setState({ phoneTooltipTitle: 'Click to copy phone number' });
                }
              }}
              placement="right"
            >
              <IconField
                icon="dashboardPhone"
                className="clickable"
                onClick={() => {
                  copyToClipboard(formatPhoneNumber(phoneNumber));
                  this.setState({ phoneTooltipTitle: 'Copied' });
                }}
              >
                {formatPhoneNumber(phoneNumber)}
              </IconField>
            </Tooltip>
          )}
          {city && state && (
            <IconField icon="geoPin">
              <div>{`${city}, ${state}`}</div>
            </IconField>
          )}
          {secondaryClient && (
            <Tooltip
              title={spouseNameTooltipTitle}
              onVisibleChange={(visible) => {
                if (!visible) {
                  this.setState({ spouseNameTooltipTitle: 'Click to copy spouse name' });
                }
              }}
              placement="right"
            >
              <IconField
                icon="dashboardSpouse"
                className="clickable"
                onClick={() => {
                  copyToClipboard(`${secondaryClient.attr('firstName')} ${secondaryClient.attr('lastName')}`);
                  this.setState({ spouseNameTooltipTitle: 'Copied' });
                }}
              >
                <div className="client-info">
                  <SecondaryClientLabelPicker secondaryClient={secondaryClient} secondaryClientLabel={secondaryClientLabel} />
                  <div>
                    {secondaryClient.attr('birthDate')
                      && getBirthdayAndAge(secondaryClient.attr('birthDate'))}
                  </div>
                </div>
              </IconField>
            </Tooltip>
          )}
          {dependents && (
            <IconField
              className="dependents"
              icon="dashboardDependents"
              onClick={() => {
                addQuery({ modal: 'dependentsModal' });
                addQuery({ dependentsLink });
              }}
            >
              <OverflowWrapper overflow={<span>...</span>}>
                <span>
                  {dependents.map(c => (
                    <span className="child" key={c.id()}>
                      <div className="client-info">
                        <div>{`${c.attr('firstName')} `}</div>
                        <div>
                          {c.attr('birthdate')
                          && getBirthdayAndAge(c.attr('birthdate'))}
                        </div>
                      </div>
                    </span>
                  ))}
                </span>
              </OverflowWrapper>
            </IconField>
          )}
          <IconField className="timezone" icon="dashboardTimeZone">
            <TimeZone />
          </IconField>
          {billingRate && billingFrequency && (
            <IconField className="serviceLevel" icon="dashboardServiceLevel">
              <CurrencyFormat amount={billingRate / 100} />
              <span>,&nbsp;{`paid ${billingFrequency}`}</span>
            </IconField>
          )}
        </div>
      </div>
    );
  }
}

Demographics.contextType = SwaggerlessContext;

Demographics.propTypes = {
  className: PropTypes.string,
  householdResource: PropTypes.object.isRequired,
  primaryClient: PropTypes.object.isRequired,
  spouseClient: PropTypes.object,
};

Demographics.defaultProps = {
  className: '',
  spouseClient: null,
};

export default withHouseholdContextAdapter(Demographics);
